/**
 * The AI board's URL vocabulary (dashboard design rules §4.2): one place for the
 * five routes, the in-page anchors and every search param the panels and their
 * drill-downs share, so a KPI tile, a donut segment, a "View all N →" link and a
 * table twin can never disagree about where they point.
 *
 * **Why the quota params matter more here than on any other board.** `/ai/capacity`
 * carries on the order of a thousand quota rows, most of them sitting at `0` of
 * their limit. §1 caps an L1 panel at aggregates plus a worst-N, so the full list
 * is a *drill*, and a drill has to live in the URL or it is not shareable and the
 * back button does not undo it. `QUOTA_SCOPE_PARAM` selects which population is
 * being listed and `QUOTA_PAGE_PARAM` pages through it — both on the capacity
 * route itself, because there is no sixth route to put them on.
 */

/** The board's entry route — the L1 overview. */
export const AI_OVERVIEW_PATH = "/ai/overview";
/** L2: every discovered AI service account. */
export const AI_ESTATE_PATH = "/ai/estate";
/** L1: quota headroom and the HTTP 429 rate — the board's most actionable panel. */
export const AI_CAPACITY_PATH = "/ai/capacity";
/** L1/L2: MCP server fleet health. */
export const AI_MCP_PATH = "/ai/mcp";
/** L1: month-to-date spend on the discovered estate. */
export const AI_SPEND_PATH = "/ai/spend";

/** Microsoft 365 Copilot adoption — a different upstream, its own page. */
export const AI_COPILOT_PATH = "/ai/copilot";

/** Anthropic Claude usage — a different upstream again, its own page. */
export const AI_CLAUDE_PATH = "/ai/claude";

/**
 * The `/ai/*` paths that have a page component today.
 *
 * Five routes are declared above — that is the board's intended vocabulary — but
 * only the overview has a page so far, and a link that 404s is worse than no
 * link (§4.1: no dead aggregates). So this is the list the sub-nav renders and
 * the list `aiHref` trusts. Shipping `/ai/capacity` is one string here: every
 * KPI tile and cross-panel link that names the route starts resolving to it,
 * with no change at any call site.
 */
export const AI_BUILT_PATHS: readonly string[] = [
  AI_OVERVIEW_PATH,
  AI_COPILOT_PATH,
  AI_CLAUDE_PATH,
];

/** Whether a declared `/ai/*` route has a page behind it yet. */
export function isAiRouteBuilt(path: string): boolean {
  return AI_BUILT_PATHS.includes(path);
}

/**
 * Where a link that names a route should actually go.
 *
 * While a route has no page, the link falls back to the anchor of the section
 * that carries the same content on the overview — so the door still opens onto
 * the panel that explains the number, rather than onto a 404.
 */
export function aiHref(path: string, anchor?: string): string {
  if (isAiRouteBuilt(path)) {
    return anchor ? `${path}#${anchor}` : path;
  }
  return anchor ? `#${anchor}` : AI_OVERVIEW_PATH;
}

export interface AiRoute {
  path: string;
  /** Sub-nav label. */
  label: string;
  /** What this route answers, used as the page subtitle. */
  subtitle: string;
}

/**
 * Every AI route in nav order, with the subtitle each page renders.
 *
 * Shared by the sub-nav and by every page's `PageHeader`, so a route cannot be
 * described one way in the nav and another way on the page it opens.
 */
export const AI_ROUTES: readonly AiRoute[] = [
  {
    path: AI_OVERVIEW_PATH,
    label: "Overview",
    subtitle:
      "The Azure AI estate as measured today: what is throttled, what is out of quota, what is provisioned and idle, and what nothing is watching.",
  },
  {
    path: AI_COPILOT_PATH,
    label: "Copilot usage",
    subtitle:
      "Microsoft 365 Copilot adoption across the organisation: seats enabled against seats licensed, active use over the period, and which apps that use lands in. Org totals only — this board has no per-person view and no route that takes one.",
  },
  {
    path: AI_CLAUDE_PATH,
    label: "Claude usage",
    subtitle:
      "Anthropic Claude usage and spend for the organisation. The claude.ai Team plan exposes no metrics API, so this reads a Claude Console organisation's Admin API and says so plainly until one exists.",
  },
  {
    path: AI_ESTATE_PATH,
    label: "Accounts",
    subtitle:
      "Every AI service account discovery returned, with its deployments, usage and observability — discovered by resource type, so a new kind appears without a deploy.",
  },
  {
    path: AI_CAPACITY_PATH,
    label: "Capacity",
    subtitle:
      "Model quota headroom and the per-account HTTP 429 rate. Quota rows are ranked by remaining headroom, not by name.",
  },
  {
    path: AI_MCP_PATH,
    label: "MCP servers",
    subtitle:
      "MCP servers identified from the control plane and probed over HTTP. ARM's verdict and the probe's verdict are reported separately.",
  },
  {
    path: AI_SPEND_PATH,
    label: "Spend",
    subtitle:
      "Month-to-date spend on the discovered accounts, grouped by whatever meter Cost Management is billing today rather than by a meter name in the code.",
  },
] as const;

/* ── In-page anchors (the KPI tiles' doors, §4.1) ───────────────────────── */

/** Overview: what the board wants a human to look at, worst first. */
export const FINDINGS_ANCHOR = "ai-findings";
/** Overview: composition by kind, region and resource type. */
export const COMPOSITION_ANCHOR = "ai-composition";
/** Capacity: quota headroom. */
export const QUOTA_ANCHOR = "ai-quota";
/** Capacity: the HTTP 429 rate per account. */
export const THROTTLE_ANCHOR = "ai-throttle";
/** Estate: the observability coverage columns. */
export const OBSERVABILITY_ANCHOR = "ai-observability";
/** Overview: org-level Microsoft 365 Copilot adoption. */
export const COPILOT_ANCHOR = "ai-copilot";

/* ── Drill params ───────────────────────────────────────────────────────── */

/**
 * Which quota population `/ai/capacity` is listing in full.
 *
 * Absent is the L1 default: aggregates plus the worst rows by headroom. Every
 * other value opens a full, paged list — an L2 view on the same route.
 */
export const QUOTA_SCOPE_PARAM = "quotas";

/** 1-based page within the selected quota population. */
export const QUOTA_PAGE_PARAM = "qpage";

/** Which deployment population `/ai/capacity` is listing in full. */
export const DEPLOYMENT_SCOPE_PARAM = "deployments";

/** Multi-value param scoping the estate grid to one or more ARM `kind` values. */
export const KIND_PARAM = "kind";

/** Multi-value param scoping the estate grid to one or more Azure regions. */
export const REGION_PARAM = "region";

/** Single-value param carrying the account whose detail drawer is open. */
export const ACCOUNT_DRAWER_PARAM = "account";

/** Single-value param carrying the MCP server whose detail drawer is open. */
export const SERVER_DRAWER_PARAM = "server";

/**
 * The quota populations a reader can list in full, and what each one *is*.
 *
 * `exhausted` and `risk` are the reason the board exists; `active` is every row
 * with any consumption at all; `all` includes the ~93% of rows that sit at zero
 * of their limit. The `all` option is deliberately last and deliberately
 * labelled with its size, so opening it is a choice rather than the default
 * altitude (§1).
 */
export const QUOTA_SCOPES = ["exhausted", "risk", "active", "unknown", "all"] as const;

export type QuotaScope = (typeof QUOTA_SCOPES)[number];

/** Human label per quota scope, used on the scope chips and in the caption. */
export const QUOTA_SCOPE_LABEL: Record<QuotaScope, string> = {
  exhausted: "Exhausted",
  risk: "At risk",
  active: "Any consumption",
  unknown: "Limit unknown",
  all: "Every row",
};

/** Rows per page in every full list on this board. */
export const PAGE_SIZE = 25;

/** Narrow a raw search-param value to a quota scope, or `null` for the L1 view. */
export function parseQuotaScope(value: string | undefined): QuotaScope | null {
  return QUOTA_SCOPES.find((scope) => scope === value) ?? null;
}

/** Parse a 1-based page from the URL, defaulting to the first page. */
export function parsePage(value: string | undefined): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

/**
 * Board-unique ids for the chart table twins (`?data=<id>`, §4.4).
 *
 * Collected here rather than spelled inline at each chart because they share one
 * URL namespace with the drill params above: two charts with the same id would
 * open each other's grid.
 */
export const TWIN_IDS = {
  byKind: "ai-by-kind",
  byRegion: "ai-by-region",
  byType: "ai-by-type",
  quotaHeadroom: "ai-quota-headroom",
  throttleRate: "ai-throttle-rate",
  deploymentQuota: "ai-deployment-quota",
  mcpProbe: "ai-mcp-probe",
  spendMeter: "ai-spend-meter",
  spendAccount: "ai-spend-account",
  copilotTrend: "ai-copilot-trend",
  copilotApps: "ai-copilot-apps",
} as const;
